import React, { useState, useEffect, useReducer } from "react";
import openSocket from "../../services/socket-io";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
} from "@material-ui/core";
import { Edit, DeleteOutline } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import FollowUpRuleModal from "../../components/FollowUpRuleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
  if (action.type === "LOAD_RULES") {
    const rules = action.payload;
    const newRules = [];

    rules.forEach((rule) => {
      const ruleIndex = state.findIndex((r) => r.id === rule.id);
      if (ruleIndex !== -1) {
        state[ruleIndex] = rule;
      } else {
        newRules.push(rule);
      }
    });

    return [...state, ...newRules];
  }

  if (action.type === "UPDATE_RULES") {
    const rule = action.payload;
    const ruleIndex = state.findIndex((r) => r.id === rule.id);

    if (ruleIndex !== -1) {
      state[ruleIndex] = rule;
      return [...state];
    } else {
      return [rule, ...state];
    }
  }

  if (action.type === "DELETE_RULES") {
    const ruleId = action.payload;

    const ruleIndex = state.findIndex((r) => r.id === ruleId);
    if (ruleIndex !== -1) {
      state.splice(ruleIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const FollowUpRules = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [rules, dispatch] = useReducer(reducer, []);
  const [selectedRule, setSelectedRule] = useState(null);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [deletingRule, setDeletingRule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchRules = async () => {
      try {
        const { data } = await api.get("/followUpRules");
        dispatch({ type: "LOAD_RULES", payload: data });
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    };
    dispatch({ type: "RESET" });
    fetchRules();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("followUpRule", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_RULES", payload: data.rule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_RULES", payload: +data.ruleId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenRuleModal = () => {
    setSelectedRule(null);
    setRuleModalOpen(true);
  };

  const handleCloseRuleModal = () => {
    setSelectedRule(null);
    setRuleModalOpen(false);
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule.id);
    setRuleModalOpen(true);
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await api.delete(`/followUpRules/${ruleId}`);
      toast.success(i18n.t("followUpRules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingRule(null);
  };

  const handleToggleActive = async (rule) => {
    try {
      const { data } = await api.put(`/followUpRules/${rule.id}`, {
        active: !rule.active,
      });
      dispatch({ type: "UPDATE_RULES", payload: data });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingRule &&
          `${i18n.t("followUpRules.confirmationModal.deleteTitle")} ${
            deletingRule.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteRule(deletingRule.id)}
      >
        {i18n.t("followUpRules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <FollowUpRuleModal
        open={ruleModalOpen}
        onClose={handleCloseRuleModal}
        ruleId={selectedRule}
      />
      <MainHeader>
        <Title>{i18n.t("followUpRules.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenRuleModal}
          >
            {i18n.t("followUpRules.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {i18n.t("followUpRules.table.name")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("followUpRules.table.whatsapp")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("followUpRules.table.funnelStage")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("followUpRules.table.hoursThreshold")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("followUpRules.table.resolveAfterClientHours")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("followUpRules.table.quickAnswer")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("followUpRules.table.active")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("followUpRules.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {rules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell align="center">{rule.name}</TableCell>
                  <TableCell align="center">{rule.whatsapp?.name}</TableCell>
                  <TableCell align="center">
                    {rule.funnelStage?.name ||
                      i18n.t("followUpRuleModal.form.funnelStageAny")}
                  </TableCell>
                  <TableCell align="center">{rule.hoursThreshold}h</TableCell>
                  <TableCell align="center">
                    {rule.resolveAfterClientHours
                      ? `${rule.resolveAfterClientHours}h`
                      : "—"}
                  </TableCell>
                  <TableCell align="center">
                    {rule.quickAnswer?.shortcut}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={rule.active}
                      onChange={() => handleToggleActive(rule)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditRule(rule)}
                    >
                      <Edit />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingRule(rule);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={7} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default FollowUpRules;
